import React, { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { apiClient } from '../../api/client';

interface CompleteProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const CompleteProfileModal: React.FC<CompleteProfileModalProps> = ({ isOpen, onClose, onComplete }) => {
  const { user, updateUser } = useAuthStore();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');  // 실명
  const [birthDate, setBirthDate] = useState('');  // 생년월일
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) {
      return numbers;
    }
    if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    }
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  const formatBirthDate = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 4) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
    } else {
      return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6, 8)}`;
    }
  };

  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatBirthDate(e.target.value);
    setBirthDate(formatted);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!phone.trim()) {
      setError('전화번호를 입력해주세요.');
      return;
    }

    const phoneNumbers = phone.replace(/[^\d]/g, '');
    if (phoneNumbers.length !== 11) {
      setError('올바른 전화번호를 입력해주세요. (11자리)');
      return;
    }

    if (!name.trim()) {
      setError('실명을 입력해주세요.');
      return;
    }

    if (!birthDate.trim()) {
      setError('생년월일을 입력해주세요.');
      return;
    }

    const birthDateNumbers = birthDate.replace(/[^\d]/g, '');
    if (birthDateNumbers.length !== 8) {
      setError('올바른 생년월일을 입력해주세요. (8자리)');
      return;
    }

    setIsSubmitting(true);

    try {
      // 백엔드에 사용자 정보 업데이트
      await apiClient.post('/users/', {
        kakao_id: String(user?.kakaoId),
        email: user?.email,
        nickname: user?.nickname,  // 카카오 닉네임 유지
        name: name.trim(),  // 실명
        phone: phone.trim(),
        birth_date: birthDate.trim(),  // 생년월일
      });

      // 로컬 스토어 업데이트 (실명과 생년월일은 별도 저장 안함, nickname만 표시용)
      updateUser({
        phone: phone.trim(),
      });

      onComplete();
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError('정보 저장에 실패했습니다. 다시 시도해주세요.');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="mb-3">
              <svg
                className="w-12 h-12 text-blue-600 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">추가 정보 입력</h2>
            <p className="text-gray-600 text-sm">
              원활한 서비스 이용을 위해 추가 정보를 입력해주세요.
            </p>
          </div>

          {/* Kakao Info Display */}
          {(user?.nickname || user?.email) && (
            <div className="p-3 bg-gray-50 rounded-lg mb-6">
              <div className="space-y-2">
                {user?.nickname && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>카카오 닉네임: <strong>{user.nickname}</strong></span>
                  </div>
                )}
                {user?.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>{user.email}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  실명 <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">예약 및 계약서 작성에 사용됩니다</span>
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="실명을 입력하세요"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Birth Date */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  생년월일 <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">본인 확인을 위해 필요합니다</span>
              </div>
              <input
                type="text"
                value={birthDate}
                onChange={handleBirthDateChange}
                placeholder="1990-01-01"
                maxLength={10}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  휴대폰번호 <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">예약 확인 및 알림용</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="010-1234-5678"
                maxLength={13}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="space-y-2 pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '저장 중...' : '완료'}
              </button>

              <button
                type="button"
                onClick={onClose}
                className="w-full bg-white text-gray-700 py-3 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                나중에 입력하기
              </button>
            </div>
          </form>

          {/* Info */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              입력하신 정보는 마이페이지에서 언제든지 수정할 수 있습니다.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfileModal;
